<template>
    <div class="card card-gray p-3">
        <div class="row">
            <div class="col-md-2">
                <label class="form-label">
                    {{ $t('reconstructions.type') }}:
                </label>
                <select v-model="type" class="form-select custom-select mb-0">
                    <option v-for="option in typeOptions" :value="option">
                      {{ $t('reconstructions.types.' + option) }}
                    </option>
                </select>
            </div>
            <div class="col-md-2">
                <label class="form-label">
                  {{ $t('reconstructions.year') }}:
                </label>
                <input class="form-control mb-0" type="number" min="1" v-model="year">
            </div>
            <div class="col-md-2">
                <label class="form-label">
                    {{ $t('reconstructions.extent') }}:
                </label>
                <select v-model="extent" class="form-select custom-select mb-0">
                    <option v-for="option in extentOptions" :value="option">
                        {{ $t('reconstructions.extents.' + option) }}
                    </option>
                </select>
            </div>
            <div class="col-md-4">
                <label class="form-label">
                    {{ $t('reconstructions.description') }}:
                </label>
                <input class="form-control w-100 mb-0" type="text" v-model="description">
            </div>
            <div class="col-md-2 btn-erase-reconstruction d-flex">
                <button @click="removeReconstruction(value)"
                        class="btn btn-outline-secondary mt-auto"
                        :title="$t('form.buttonRemove')">
                    <span class="fas fa-times"></span>
                </button>
            </div>
        </div>
        <div v-if="error" class="invalid-feedback d-block">{{ error }}</div>
    </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, PropType, ref, watch} from 'vue';
import {TranslateResult, useI18n} from 'vue-i18n';
import {Reconstruction} from './values';

export default defineComponent({
    props: {
        typeOptions: {
            type: Array as PropType<string[]>,
            required: true,
        },
        extentOptions: {
            type: Array as PropType<string[]>,
            required: true,
        },
        value: {
            type: Object as PropType<Reconstruction | null>,
            default: null,
        },
        validate: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['input', 'remove'],
    setup(props, {emit}) {
        const {t} = useI18n();

        const type = ref('0');
        const extent = ref('0');
        const year = ref('');
        const description = ref<string | null>(null);

        function getError(): TranslateResult | null {
            if (type.value === '0') {
                return t('form.errors.reconstructions.typeNotSelected');
            }

            if (extent.value === '0') {
                return t('form.errors.reconstructions.extentNotSelected');
            }

            if (year.value === '') {
                return t('form.errors.reconstructions.yearNotFilled');
            }

            const parsedYear = parseInt(year.value, 10);

            if (isNaN(parsedYear)) {
                return t('form.errors.reconstructions.invalidYear');
            }

            return null;
        }

        const newValue = computed((): Reconstruction | null => {
            if (getError() !== null) {
                return null;
            }

            return {
                year: parseInt(year.value, 10),
                type: type.value,
                extent: extent.value,
                description: description.value,
            };
        });

        const error = computed((): TranslateResult | null => {
            return props.validate
                ? getError()
                : null;
        });

        function removeReconstruction(): void {
            emit('remove');
        }

        onMounted(() => {
            const value = props.value;

            if (value === null) {
                type.value = Object.keys(props.typeOptions)[0];
                extent.value = Object.keys(props.extentOptions)[0];
                return;
            }

            type.value = value.type;
            extent.value = value.extent;
            year.value = '' + value.year;
            description.value = value.description;
        });

        watch(
            [type, extent, year, description],
            () => {
                emit('input', newValue.value);
            },
        );

        return {
            type,
            extent,
            year,
            description,
            error,
            removeReconstruction,
        };
    },
});
</script>
